import React, { useState, useMemo } from "react";
import {
  FaSearch,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUsers,
  FaClock,
} from "react-icons/fa";

const RestaurantBookingSearchForm = ({
  onSearch,
  openingTime = "10:00 AM",
  closingTime = "11:00 PM",
}) => {
  const [formData, setFormData] = useState({
    restaurant: "",
    guests: "",
    bookingDate: "",
    timeSlot: "",
  });

  const [errors, setErrors] = useState({});

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split("T")[0];

  // Generate time slots dynamically
  const timeSlots = useMemo(() => {
    const slots = [];

    // Convert opening and closing times to 24-hour format
    const parseTime = (timeStr) => {
      const [time, modifier] = timeStr.split(" ");
      let [hours, minutes] = time.split(":").map(Number);

      if (modifier === "PM" && hours !== 12) {
        hours += 12;
      } else if (modifier === "AM" && hours === 12) {
        hours = 0;
      }

      return { hours, minutes };
    };

    const formatTime = (hours, minutes) => {
      const period = hours >= 12 ? "PM" : "AM";
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${period}`;
    };

    const opening = parseTime(openingTime);
    const closing = parseTime(closingTime);

    let currentHour = opening.hours;
    let currentMinute = opening.minutes;

    while (
      currentHour < closing.hours ||
      (currentHour === closing.hours && currentMinute <= closing.minutes)
    ) {
      const startTime = formatTime(currentHour, currentMinute);

      // Calculate end time (1 hour later)
      let endHour = currentHour + 1;
      let endMinute = currentMinute;

      if (endHour > 23) {
        endHour = 0;
      }

      const endTime = formatTime(endHour, endMinute);

      // Stop if end time exceeds closing time
      if (
        endHour > closing.hours ||
        (endHour === closing.hours && endMinute > closing.minutes)
      ) {
        break;
      }

      slots.push({
        value: `${startTime} - ${endTime}`,
        label: `${startTime} - ${endTime}`,
      });

      // Move to next hour
      currentHour += 1;
    }

    return slots;
  }, [openingTime, closingTime]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Restaurant validation
    if (!formData.restaurant.trim()) {
      newErrors.restaurant = "Please enter restaurant name or address";
    }

    // Guests validation
    if (!formData.guests || parseInt(formData.guests) < 1) {
      newErrors.guests = "Number of guests must be at least 1";
    }

    // Booking date validation
    if (!formData.bookingDate) {
      newErrors.bookingDate = "Please select booking date";
    } else if (formData.bookingDate < today) {
      newErrors.bookingDate = "Booking date cannot be in the past";
    }

    // Time slot validation
    if (!formData.timeSlot) {
      newErrors.timeSlot = "Please select a time slot";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRestaurantSearch = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const searchData = {
        restaurant: formData.restaurant.trim(),
        guests: parseInt(formData.guests),
        bookingDate: formData.bookingDate,
        timeSlot: formData.timeSlot,
      };

      // Call parent handler
      if (onSearch) {
        onSearch(searchData);
      }
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-4 md:p-6 transition-all duration-500">
      {/* Header - Refined & Compact */}
      <div className="text-center mb-6 md:mb-8">
        <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-1 tracking-tight">
          Reserve Your Table
        </h2>
        <p className="text-xs md:text-sm text-gray-500 font-medium uppercase tracking-[0.1em]">
          Premium Dining Reservation System
        </p>
      </div>

      {/* Search Form - Elegant & Compact */}
      <form
        onSubmit={handleRestaurantSearch}
        className="bg-white rounded-[24px] shadow-[0_20px_50px_rgba(0,0,0,0.08)] p-5 md:p-8 border border-gray-50 relative group"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {/* Restaurant Name/Address Input */}
          <div className="lg:col-span-2">
            <label className="block text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 md:mb-2 ml-1">
              Location / Restaurant
            </label>
            <div className="relative">
              <FaMapMarkerAlt className="absolute left-4 top-1/2 -translate-y-1/2 text-red-500 opacity-60 z-10" />
              <input
                type="text"
                name="restaurant"
                value={formData.restaurant}
                onChange={handleInputChange}
                placeholder="Where would you like to dine?"
                className={`w-full pl-10 pr-4 py-3 rounded-2xl border-2 transition-all duration-300 focus:outline-none focus:ring-4 ${
                  errors.restaurant
                    ? "border-red-100 focus:border-red-500 focus:ring-red-50"
                    : "border-gray-50 focus:border-red-500 bg-gray-50/30"
                } text-sm font-bold text-gray-800`}
              />
            </div>
            {errors.restaurant && (
              <p className="text-red-500 text-[10px] font-bold mt-1.5 ml-1">{errors.restaurant}</p>
            )}
          </div>

          {/* Number of Guests */}
          <div>
            <label className="block text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 md:mb-2 ml-1">
              Party Size
            </label>
            <div className="relative">
              <FaUsers className="absolute left-4 top-1/2 -translate-y-1/2 text-green-500 opacity-60 z-10" />
              <input
                type="number"
                name="guests"
                min="1"
                max="20"
                value={formData.guests}
                onChange={handleInputChange}
                placeholder="Guests"
                className={`w-full pl-10 pr-4 py-3 rounded-2xl border-2 transition-all duration-300 focus:outline-none focus:ring-4 ${
                  errors.guests
                    ? "border-red-100 focus:border-red-500 focus:ring-red-50"
                    : "border-gray-50 focus:border-green-500 bg-gray-50/30"
                } text-sm font-bold text-gray-800`}
              />
            </div>
            {errors.guests && (
              <p className="text-red-500 text-[10px] font-bold mt-1.5 ml-1">{errors.guests}</p>
            )}
          </div>

          {/* Booking Date */}
          <div>
            <label className="block text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5 md:mb-2 ml-1">
              Dining Date
            </label>
            <div className="relative">
              <FaCalendarAlt className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-500 opacity-60 z-10" />
              <input
                type="date"
                name="bookingDate"
                min={today}
                value={formData.bookingDate}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 rounded-2xl border-2 transition-all duration-300 focus:outline-none focus:ring-4 ${
                  errors.bookingDate
                    ? "border-red-100 focus:border-red-500 focus:ring-red-50"
                    : "border-gray-50 focus:border-purple-500 bg-gray-50/30"
                } text-sm font-bold text-gray-800`}
              />
            </div>
            {errors.bookingDate && (
              <p className="text-red-500 text-[10px] font-bold mt-1.5 ml-1">{errors.bookingDate}</p>
            )}
          </div>
        </div>

        {/* Time Slot Selection */}
        <div className="mt-6 md:mt-8">
          <label className="block text-[10px] md:text-xs font-black text-gray-400 uppercase tracking-widest mb-3 md:mb-4 ml-1">
            Available Time Slots <span className="text-blue-500/70 ml-1">({openingTime} - {closingTime})</span>
          </label>

          {timeSlots.length > 0 ? (
            <>
              {/* Desktop: Dropdown */}
              <div className="hidden md:block">
                <select
                  name="timeSlot"
                  value={formData.timeSlot}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.timeSlot
                      ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                      : "border-gray-200 focus:border-blue-400 hover:border-gray-300"
                  }`}
                >
                  <option value="">Select a time slot</option>
                  {timeSlots.map((slot) => (
                    <option key={slot.value} value={slot.value}>
                      {slot.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mobile: Chip-style buttons */}
              <div className="md:hidden">
                <div className="grid grid-cols-2 gap-2">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot.value}
                      type="button"
                      onClick={() =>
                        handleInputChange({
                          target: { name: "timeSlot", value: slot.value },
                        })
                      }
                      className={`py-2.5 px-3 rounded-xl text-[10px] font-black tracking-wider transition-all duration-300 uppercase ${
                        formData.timeSlot === slot.value
                          ? "bg-blue-600 text-white shadow-lg ring-4 ring-blue-50"
                          : "bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-100"
                      }`}
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <p className="text-gray-500 italic">
              No time slots available for the selected hours.
            </p>
          )}

          {errors.timeSlot && (
            <p className="text-red-500 text-sm mt-2">{errors.timeSlot}</p>
          )}
        </div>

        {/* Search Button */}
        <div className="mt-8 md:mt-10 text-center">
          <button
            type="submit"
            className="w-full md:w-auto min-w-[240px] bg-gradient-to-r from-[#EE2E24] to-[#FFC400] hover:brightness-110 text-white font-black py-4 px-10 rounded-2xl shadow-[0_10px_30px_rgba(238,46,36,0.25)] hover:shadow-[0_15px_40px_rgba(238,46,36,0.35)] transform hover:-translate-y-1 transition-all duration-500 flex items-center justify-center mx-auto text-sm uppercase tracking-widest relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></div>
            <div className="absolute -left-10 top-0 w-20 h-full bg-white/10 skew-x-[-25deg] group-hover:left-[110%] transition-all duration-1000"></div>
            <FaSearch className="mr-3 text-xs" />
            Find Your Table
          </button>
        </div>

        {/* Additional Info */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            🍽️ Instant table reservation • 🔒 Secure booking • 📱 Digital
            confirmation
          </p>
        </div>
      </form>
    </div>
  );
};

export default RestaurantBookingSearchForm;
